import React, { useEffect, useMemo } from "react";
import { Typography, Tooltip, Chip, Box } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";

import { BaseTabs } from "components/Shared/BaseTabs";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import TableFilter from "components/Shared/AppTable/TableFilter";
import HighlightedText from "components/Shared/HighlightedText";

import { useDriverDetailContext } from "contexts/DriverDetailContext";
import { useTablePagination } from "hooks";
import { useLazyGetDriverAssignmentsQuery, useLazyGetDriverToUnloadShipmentsQuery } from "store/services/driverApi";

const DriverAssignmentShipmentsTabs = () => {
  const { driverDetail } = useDriverDetailContext();
  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "ToUnload";
  const keyword = searchParams.get("search") || "";

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();

  const [fetchAssignments, { data: assignmentsData, isFetching: isFetchingAssignments }] =
    useLazyGetDriverAssignmentsQuery();
  const [fetchToUnload, { data: toUnloadData, isFetching: isFetchingToUnload }] =
    useLazyGetDriverToUnloadShipmentsQuery();
  const data = activeTab === "ToUnload" ? toUnloadData : assignmentsData;
  const isFetching = activeTab === "ToUnload" ? isFetchingToUnload : isFetchingAssignments;
  const tabs = [
    { label: "To Unload", value: "ToUnload" },
    { label: "Collection", value: "Collection" },
    { label: "All Loading Records", value: "Loading" },
    { label: "Send Out", value: "SendOut" },
  ];

  // Reset pagination when switching tabs
  useEffect(() => {
    handlePageChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (!driverDetail?._id) return;
    if (activeTab === "ToUnload") {
      fetchToUnload({
        driverId: driverDetail._id,
        page: currentPage,
        limit: perPage,
        ...(keyword && { keyword }),
      });
    } else {
      fetchAssignments({
        driverId: driverDetail._id,
        type: activeTab,
        page: currentPage,
        limit: perPage,
        ...(keyword && { keyword }),
      });
    }
  }, [driverDetail?._id, activeTab, currentPage, perPage, keyword, fetchAssignments, fetchToUnload]);

  // Shared Shipment Cell Renderer
  const renderShipmentCell = (row) => {
    const shipments = row?.shipmentsData || [];
    if (shipments.length === 0) return <Typography variant="body2">N/A</Typography>;

    const displayShipments = shipments.slice(0, 2);
    const remainingCount = shipments.length - 2;

    return (
      <div className="flex flex-wrap gap-1 items-center py-1">
        {displayShipments.map((s, index) => (
          <Link
            key={s._id || index}
            to={`/app/crm/shipment/${s._id}/detail`}
            className="hover:opacity-80"
          >
            <Chip
              label={<HighlightedText text={s.shipmentId} highlight={keyword} />}
              size="small"
              variant="outlined"
              color="primary"
              className="cursor-pointer"
            />
          </Link>
        ))}

        {remainingCount > 0 && (
          <Tooltip
            title={
              <div className="flex flex-col gap-1">
                {shipments.slice(2).map((s) => (
                  <span key={s._id}>{s.shipmentId}</span>
                ))}
              </div>
            }
            arrow
            placement="top"
          >
            <Chip label={`+${remainingCount} more`} size="small" />
          </Tooltip>
        )}
      </div>
    );
  };

  const columnConfig = useMemo(() => ({
    Collection: [
      {
        name: "Collection ID",
        selector: (row) => row.collectionId,
        sortable: true,
      },
      {
        name: "Shipment IDs",
        cell: renderShipmentCell,
        minWidth: "250px",
      },
      {
        name: "Pickup Time",
        selector: (row) => <AppTableDateTime date={row?.actualPickupTime} />,
      },
      {
        name: "Created At",
        selector: (row) => <AppTableDateTime date={row?.createdAt} />,
      },
    ],
    Loading: [
      {
        name: "Loading ID",
        selector: (row) => row.loadingId,
        sortable: true,
      },
      {
        name: "Shipment IDs",
        cell: renderShipmentCell,
        minWidth: "250px",
      },
      {
        name: "Loaded Branch",
        selector: (row) => row.loadedBranchData?.name || "N/A",
      },
      {
        name: "Loading Time",
        selector: (row) => <AppTableDateTime date={row?.loadingTime} />,
      },
      {
        name: "Created At",
        selector: (row) => <AppTableDateTime date={row?.createdAt} />,
      },
    ],
    SendOut: [
      {
        name: "SendOut ID",
        selector: (row) => row.sendOutId,
        sortable: true,
      },
      {
        name: "Shipment IDs",
        cell: renderShipmentCell,
        minWidth: "250px",
      },
      {
        name: "Description",
        selector: (row) => row.description || "N/A",
      },
      {
        name: "Created At",
        selector: (row) => <AppTableDateTime date={row?.createdAt} />,
      },
    ],
    ToUnload: [
      {
        name: "Branch",
        selector: (row) => row.branchName,
        sortable: true,
      },
      {
        name: "Branch Type",
        selector: (row) => (row.branchType === "transit" ? "Transit" : "Destination"),
      },
      {
        name: "Shipment Count",
        selector: (row) => row.shipmentCount,
      },
      {
        name: "Shipment IDs",
        cell: renderShipmentCell,
        minWidth: "250px",
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [keyword]);

  const currentColumns = columnConfig[activeTab] || [];

  return (
    <>
      <BaseTabs tabs={tabs} defaultValue={activeTab} />

       <Box position="relative">
        {activeTab === "ToUnload" && (
          <Box
            position="absolute"
            top="50%"
            left={{ xs: 16, sm: 32 }}
            sx={{ transform: "translateY(-50%)" }}
            display={{ xs: "none", sm: "flex" }}
            gap={1}
            zIndex={1}
          >
            {[
              { label: "Shipments", value: toUnloadData?.totalShipments || 0 },
              { label: "Branches", value: toUnloadData?.totalItems || 0 },
            ].map(({ label, value }) => (
              <Box
                key={label}
                display="flex"
                flexDirection="column"
                alignItems="center"
                px={2}
                py={0.5}
                bgcolor="action.hover"
                borderRadius={2}
                minWidth={72}
              >
                <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
                  {label}
                </Typography>
                <Typography variant="subtitle1" fontWeight={700} color="text.primary" whiteSpace="nowrap">
                  {isFetchingToUnload ? "…" : value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        <TableFilter
          searchLabel={
            activeTab === "ToUnload"
              ? "Search by shipment ID or branch name..."
              : `Search shipments in ${activeTab}...`
          }
          disabled={isFetching}
        />
      </Box>
      <AppTable
        columns={currentColumns}
        data={data?.data || []}
        progressPending={isFetching}

        paginationServer
        paginationPerPage={perPage}
        paginationTotalRows={data?.totalItems || 0}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}

        noDataComponent={
          <Typography py={3} textAlign="center" color="textSecondary">
            No records found.
          </Typography>
        }
      />
    </>
  );
};

export default DriverAssignmentShipmentsTabs;